import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Phone, Building, AlertCircle, Key, CheckCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import api from '../../services/api';

// InputField component remains the same
const InputField = React.memo(({ 
  label, 
  name,
  type = 'text', 
  placeholder, 
  required = false, 
  icon: Icon, 
  autoComplete,
  value,
  onChange,
  onKeyDown,
  error,
  disabled,
  showToggle = false,
  showValue = false,
  onToggle = null 
}) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
      )}
      <input
        id={name}
        name={name}
        type={showToggle ? (showValue ? 'text' : 'password') : type}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        autoComplete={autoComplete}
        disabled={disabled}
        className={`w-full px-3 py-2 ${Icon ? 'pl-10' : ''} ${showToggle ? 'pr-10' : ''} border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
          error 
            ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        placeholder={placeholder}
      />
      {showToggle && onToggle && (
        <button
          type="button"
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          onClick={onToggle}
          disabled={disabled}
          tabIndex={-1}
        >
          {showValue ? (
            <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
          ) : (
            <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
          )}
        </button>
      )}
    </div>
    {error && (
      <p className="mt-1 text-sm text-red-600 flex items-center">
        <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
));

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Registration form, 2: Email verification
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    phone: '',
    company: '',
    agreeTerms: false, // Keep for backward compatibility
    termsAccepted: false, // ADDED: New field for service agreement
    // Verification data
    token: '',
    otp: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    let finalValue = value;
    
    // For phone field, only allow numbers and limit length
    if (name === 'phone') {
      finalValue = value.replace(/\D/g, '').slice(0, 15);
    }
    
    // For OTP field, only allow numbers and limit to 6 digits
    if (name === 'otp') {
      finalValue = value.replace(/\D/g, '').slice(0, 6);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : finalValue
    }));
    
    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
  }, []);

  // UPDATED: Enhanced validation with service agreement
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.password_confirmation) {
      newErrors.password_confirmation = 'Please confirm your password';
    } else if (formData.password_confirmation !== formData.password) {
      newErrors.password_confirmation = 'Passwords do not match';
    }

    if (formData.phone && !/^[0-9]{8,15}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 8-15 digits';
    }

    // UPDATED: Check for service agreement acceptance
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the Service Agreement to continue';
    }

    return newErrors;
  }, [formData]);

  // UPDATED: Step 1 with service agreement
  const handleRegisterSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();

    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        phone: formData.phone.trim() || null,
        company: formData.company.trim() || null,
        terms_accepted: formData.termsAccepted // ADDED: Include service agreement acceptance
      };

      const response = await api.post('/auth/register', payload);
      
      if (response.data.code === 'verification_sent' && response.data.data.token) {
        toast.success('Verification OTP sent to your email!', {
          duration: 4000,
          position: 'top-center'
        });
        setFormData(prev => ({
          ...prev,
          token: response.data.data.token
        }));
        setStep(2); // Move to verification step
      } else if (response.data.code === 'verification_pending') {
        toast.info('Verification already sent. Please check your email.', {
          duration: 4000,
          position: 'top-center'
        });
        setFormData(prev => ({
          ...prev,
          token: response.data.data.token
        }));
        setStep(2);
      }
    } catch (error) {
      console.error('Registration failed:', error);
      
      if (error.response?.status === 422 && error.response.data.errors) {
        const apiErrors = {};
        Object.keys(error.response.data.errors).forEach(key => {
          apiErrors[key] = Array.isArray(error.response.data.errors[key]) 
            ? error.response.data.errors[key][0] 
            : error.response.data.errors[key];
        });
        setErrors(apiErrors);
        
        // Show specific error for service agreement
        if (apiErrors.terms_accepted) {
          toast.error('Please accept the Service Agreement to continue.', {
            duration: 5000,
            position: 'top-center'
          });
        }
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message, {
          duration: 4000,
          position: 'top-center'
        });
      } else {
        toast.error('Registration failed. Please try again.', {
          duration: 4000,
          position: 'top-center'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm]);

  // Step 2: Verify email with OTP and complete registration
  const handleVerificationSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();

    if (!formData.otp.trim()) {
      setErrors({ otp: 'OTP is required' });
      return;
    }

    if (formData.otp.length < 4) {
      setErrors({ otp: 'Please enter a valid OTP' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const payload = {
        token: formData.token,
        otp: parseInt(formData.otp),
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        phone: formData.phone.trim() || null,
        company: formData.company.trim() || null
      };

      const response = await api.post('/auth/verify-email-complete-registration', payload);
      
      if (response.data.code === 'success') {
        toast.success('Registration completed successfully!', {
          duration: 4000,
          position: 'top-center',
          style: {
            background: '#f0f9ff',
            color: '#0c4a6e',
            border: '1px solid #0ea5e9'
          }
        });
        
        // UPDATED: Store tokens with expiration info using sessionStorage
        if (response.data.data.access) {
          sessionStorage.setItem('access_token', response.data.data.access);
        }
        if (response.data.data.refresh) {
          sessionStorage.setItem('refresh_token', response.data.data.refresh);
        }
        if (response.data.data.user) {
          sessionStorage.setItem('user', JSON.stringify(response.data.data.user));
          sessionStorage.setItem('role', response.data.data.role);
        }
        
        // Store expiration info
        if (response.data.data.expires_at) {
          sessionStorage.setItem('token_expires_at', response.data.data.expires_at);
        }
        if (response.data.data.expires_in) {
          sessionStorage.setItem('token_expires_in', response.data.data.expires_in.toString());
        }
        
        // Redirect to dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Verification failed:', error);
      
      if (error.response?.status === 422 && error.response.data.errors) {
        const apiErrors = {};
        Object.keys(error.response.data.errors).forEach(key => {
          apiErrors[key] = Array.isArray(error.response.data.errors[key]) 
            ? error.response.data.errors[key][0] 
            : error.response.data.errors[key];
        });
        setErrors(apiErrors);
      } else if (error.response?.status === 400) {
        setErrors({ otp: 'Invalid or expired OTP' });
        toast.error('Invalid or expired OTP', {
          duration: 4000,
          position: 'top-center'
        });
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message, {
          duration: 4000,
          position: 'top-center'
        });
      } else {
        toast.error('Verification failed. Please try again.', {
          duration: 4000,
          position: 'top-center'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, navigate]);

  // Resend verification OTP
  const handleResendOTP = useCallback(async () => {
    setIsSubmitting(true);
    
    try {
      const response = await api.post('/auth/resend-verification-otp', {
        email: formData.email,
        token: formData.token
      });
      
      if (response.data.code === 'success') {
        toast.success('New verification OTP sent to your email!', {
          duration: 4000,
          position: 'top-center'
        });
      }
    } catch (error) {
      toast.error('Failed to resend OTP. Please try again.', {
        duration: 4000,
        position: 'top-center'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData.email, formData.token]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      if (step === 1) handleRegisterSubmit();
      else if (step === 2) handleVerificationSubmit();
    }
  }, [handleRegisterSubmit, handleVerificationSubmit, isSubmitting, step]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword(prev => !prev);
  }, []);

  // Step indicator
  const renderStepIndicator = () => (
    <div className="flex justify-center mb-8">
      <div className="flex items-center space-x-4">
        {[1, 2].map((stepNum) => (
          <React.Fragment key={stepNum}>
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
              ${stepNum === step ? 'bg-blue-600 text-white' : 
                stepNum < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}
            `}>
              {stepNum < step ? '✓' : stepNum}
            </div>
            {stepNum < 2 && (
              <div className={`w-8 h-0.5 ${stepNum < step ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // Field props for step 1
  const fieldProps = useMemo(() => ({
    name: {
      label: "Full Name",
      name: "name",
      placeholder: "Enter your full name",
      required: true,
      icon: User,
      autoComplete: "name",
      value: formData.name,
      onChange: handleInputChange,
      onKeyDown: handleKeyDown,
      error: errors.name,
      disabled: isSubmitting
    },
    email: {
      label: "Email Address",
      name: "email",
      type: "email",
      placeholder: "Enter your email",
      required: true,
      icon: Mail,
      autoComplete: "email",
      value: formData.email,
      onChange: handleInputChange,
      onKeyDown: handleKeyDown,
      error: errors.email,
      disabled: isSubmitting
    },
    phone: {
      label: "Phone Number",
      name: "phone",
      type: "tel",
      placeholder: "Enter phone number (numbers only)",
      icon: Phone,
      autoComplete: "tel",
      value: formData.phone,
      onChange: handleInputChange,
      onKeyDown: handleKeyDown,
      error: errors.phone,
      disabled: isSubmitting
    },
    company: {
      label: "Company",
      name: "company",
      placeholder: "Enter your company name",
      icon: Building,
      autoComplete: "organization",
      value: formData.company,
      onChange: handleInputChange,
      onKeyDown: handleKeyDown,
      error: errors.company,
      disabled: isSubmitting
    },
    password: {
      label: "Password",
      name: "password",
      placeholder: "Create a password",
      required: true,
      icon: Lock,
      autoComplete: "new-password",
      value: formData.password,
      onChange: handleInputChange,
      onKeyDown: handleKeyDown,
      error: errors.password,
      disabled: isSubmitting,
      showToggle: true,
      showValue: showPassword,
      onToggle: togglePasswordVisibility
    },
    passwordConfirmation: {
      label: "Confirm Password",
      name: "password_confirmation",
      placeholder: "Confirm your password",
      required: true,
      icon: Lock,
      autoComplete: "new-password",
      value: formData.password_confirmation,
      onChange: handleInputChange,
      onKeyDown: handleKeyDown,
      error: errors.password_confirmation,
      disabled: isSubmitting,
      showToggle: true,
      showValue: showConfirmPassword,
      onToggle: toggleConfirmPasswordVisibility
    }
  }), [
    formData, 
    errors, 
    isSubmitting, 
    showPassword, 
    showConfirmPassword, 
    handleInputChange, 
    handleKeyDown, 
    togglePasswordVisibility, 
    toggleConfirmPasswordVisibility
  ]);

  // Step 1: Registration Form
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <svg
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Create your account
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Or{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                sign in to existing account
              </Link>
            </p>
          </div>

          {renderStepIndicator()}

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
            <form onSubmit={handleRegisterSubmit} className="space-y-5">
              <InputField {...fieldProps.name} />
              <InputField {...fieldProps.email} />
              <InputField {...fieldProps.phone} />
              <InputField {...fieldProps.company} />
              <InputField {...fieldProps.password} />
              <InputField {...fieldProps.passwordConfirmation} />

              {/* UPDATED: Enhanced Service Agreement Section */}
              <div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start space-x-3">
                    <div className="flex items-center h-5">
                      <input
                        id="termsAccepted"
                        name="termsAccepted"
                        type="checkbox"
                        checked={formData.termsAccepted}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                          errors.termsAccepted ? 'border-red-300' : ''
                        } disabled:cursor-not-allowed`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <label htmlFor="termsAccepted" className="text-sm font-medium text-gray-700 flex items-center">
                        I agree to the Service Agreement
                        <span className="text-red-500 ml-1">*</span>
                        <Link
                          to="/terms-of-service"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:text-blue-500 transition-colors"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </Link>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        By checking this box, I acknowledge that I have read, understood, and agree to be bound by the Terms of Service, Privacy Policy, and all applicable policies. I consent to the collection, use, and processing of my personal information as described in our policies.
                      </p>
                    </div>
                  </div>
                  
                  {/* Service Agreement Status */}
                  <div className="mt-3 flex items-center">
                    {formData.termsAccepted ? (
                      <div className="flex items-center text-green-700">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">Service Agreement accepted</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-red-600">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">Please accept the Service Agreement to continue</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {errors.termsAccepted && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                    {errors.termsAccepted}
                  </p>
                )}
              </div>

              {/* UPDATED: Submit button with service agreement check */}
              <button
                type="submit"
                disabled={isSubmitting || !formData.termsAccepted}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Sending Verification...</span>
                  </>
                ) : (
                  <span>Create Account</span>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">
                By creating an account, you'll receive an email verification code to confirm your email address and complete the registration process.
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Need help? Contact our{' '}
              <Link to="/support" className="text-blue-600 hover:text-blue-500 transition-colors">
                support team
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Email Verification (remains the same)
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Verify Your Email
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            We've sent a verification code to your email
          </p>
        </div>

        {renderStepIndicator()}

        {/* Email Sent Info */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm text-green-800 font-medium">
                Verification email sent!
              </p>
              <p className="text-sm text-green-700 mt-1">
                We've sent a verification code to <strong>{formData.email}</strong>
              </p>
              <p className="text-xs text-green-600 mt-2">
                Check your spam folder if you don't see it within a few minutes.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
          <form onSubmit={handleVerificationSubmit} className="space-y-6">
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                Verification Code <span className="text-red-500">*</span>
              </label>
              <input
                id="otp"
                name="otp"
                type="text"
                value={formData.otp}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed text-center text-lg font-mono tracking-widest ${
                  errors.otp 
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                placeholder="Enter verification code"
                maxLength="6"
                autoComplete="one-time-code"
              />
              {errors.otp && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                  {errors.otp}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 text-center">
                Enter the 4-6 digit code from your email
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !formData.otp.trim()}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader className="animate-spin h-5 w-5 text-white" />
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5" />
                  <span>Complete Registration</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="text-center space-y-2">
              <p className="text-xs text-gray-500">
                Didn't receive the code?
              </p>
              <button
                onClick={handleResendOTP}
                disabled={isSubmitting}
                className="text-sm text-blue-600 hover:text-blue-500 transition-colors font-medium disabled:text-gray-400"
              >
                Resend verification code
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => setStep(1)}
            disabled={isSubmitting}
            className="text-sm text-gray-600 hover:text-gray-900 transition-colors disabled:text-gray-400"
          >
            ← Back to registration form
          </button>
        </div>
      </div>
    </div>
  );
};

export default Register;